import { useCallback, useRef, useState, useEffect } from "react";
import Cropper from "react-easy-crop";
import { useDispatch, useSelector } from "react-redux";
import { createPost } from "../../functions/post";
import { uploadImages } from "../../functions/uploadImages";
import { updateprofilePicture } from "../../functions/user";
import getCroppedImg from "../../helpers/getCroppedImg";
import PulseLoader from "react-spinners/PulseLoader";
import Cookies from "js-cookie";

const DEFAULT_PROFILE_PIC = "/images/default_pic.png";

export default function UpdateProfilePicture({
  setImage,
  image,
  setError,
  setShow,
  pRef,
  onPictureChange,
}) {
  const dispatch = useDispatch();
  const [description] = useState("");
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [showPostConfirmation, setShowPostConfirmation] = useState(false);
  const [pendingProfileUpdate, setPendingProfileUpdate] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showCameraMenu, setShowCameraMenu] = useState(false);
  const [removing, setRemoving] = useState(false);
  const slider = useRef(null);
  const timeoutRef = useRef(null);
  const uploadInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const cameraMenuRef = useRef(null);
  const { user } = useSelector((state) => ({ ...state }));
  const [loading, setLoading] = useState(false);
  const accountPrivacy =
    (user && (user.privacy || user?.profile?.privacy)) ||
    localStorage.getItem("accountPrivacy") ||
    "public";
  const isMobile =
    typeof window !== "undefined" &&
    (window.innerWidth <= 768 ||
      window.matchMedia("(pointer: coarse)").matches);

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setShowSuccessAlert(true);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setShowSuccessAlert(false);
      setSuccessMessage("");
      timeoutRef.current = null;
    }, 4000);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!showCameraMenu) return undefined;

    const handleClickOutside = (event) => {
      if (
        cameraMenuRef.current &&
        !cameraMenuRef.current.contains(event.target)
      ) {
        setShowCameraMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [showCameraMenu]);

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const zoomIn = () => {
    slider.current.stepUp();
    setZoom(slider.current.value);
  };

  const zoomOut = () => {
    slider.current.stepDown();
    setZoom(slider.current.value);
  };

  const rotateLeft = () => setRotation((r) => (r - 90 + 360) % 360);
  const rotateRight = () => setRotation((r) => (r + 90) % 360);

  const resetCropState = () => {
    setZoom(1);
    setCrop({ x: 0, y: 0 });
    setRotation(0);
  };

  const handleReplaceImage = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (
      file.type !== "image/jpeg" &&
      file.type !== "image/png" &&
      file.type !== "image/webp" &&
      file.type !== "image/gif" &&
      file.type !== "image/avif"
    ) {
      setError?.(`${file.name} format is not supported.`);
      return;
    }
    if (file.size > 1024 * 1024 * 5) {
      setError?.(`${file.name} is too large. Max 5MB allowed.`);
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      resetCropState();
      setImage(event.target.result);
      setShowCameraMenu(false);
    };
    reader.readAsDataURL(file);
  };

  const handleUploadPhoto = () => {
    setShowCameraMenu(false);
    uploadInputRef.current?.click();
  };

  const handleTakePhoto = () => {
    setShowCameraMenu(false);
    cameraInputRef.current?.click();
  };

  const handleRemoveProfilePicture = async () => {
    setShowCameraMenu(false);
    try {
      setRemoving(true);
      const result = await updateprofilePicture("", user.token);
      if (result === "ok") {
        if (pRef?.current) {
          pRef.current.style.backgroundImage = `url(${DEFAULT_PROFILE_PIC})`;
          const img = pRef.current.querySelector("img");
          if (img) img.src = DEFAULT_PROFILE_PIC;
        }
        Cookies.set(
          "user",
          JSON.stringify({
            ...user,
            picture: "",
          })
        );
        dispatch({
          type: "UPDATEPICTURE",
          payload: "",
        });
        onPictureChange?.("");
        showSuccess("Profile picture removed.");
        setImage("");
        setShow(false);
      } else {
        setError?.(result);
      }
    } catch (error) {
      setError?.(
        error.response?.data?.message || "Failed to remove profile picture"
      );
    } finally {
      setRemoving(false);
    }
  };

  const getCroppedImage = useCallback(
    async (show) => {
      try {
        const img = await getCroppedImg(image, croppedAreaPixels, rotation);
        if (show) {
          setZoom(1);
          setCrop({ x: 0, y: 0 });
          setRotation(0);
          setImage(img);
        } else {
          return img;
        }
      } catch (error) {
        console.log(error);
      }
    },
    [croppedAreaPixels, image, rotation, setImage]
  );

  const updateProfilePictureOnly = async (imageData) => {
    try {
      const updated_picture = await updateprofilePicture(
        imageData.url,
        user.token
      );
      if (updated_picture === "ok") {
        setImage("");
        pRef.current.style.backgroundImage = `url(${imageData.url})`;
        Cookies.set(
          "user",
          JSON.stringify({
            ...user,
            picture: imageData.url,
          })
        );
        dispatch({
          type: "UPDATEPICTURE",
          payload: imageData.url,
        });
        onPictureChange?.(imageData.url);
        showSuccess("Profile picture updated successfully!");
        setShow(false);
        return true;
      }
      setError(updated_picture);
      return false;
    } catch (error) {
      setError(
        error.response?.data?.message || "Failed to update profile picture"
      );
      return false;
    }
  };

  const updateProfilePictureWithPost = async (imageData) => {
    try {
      const updated_picture = await updateprofilePicture(
        imageData.url,
        user.token
      );
      if (updated_picture === "ok") {
        const new_post = await createPost(
          "profilePicture",
          null,
          description,
          [imageData],
          user.id,
          user.token
        );
        if (new_post.status === "ok") {
          setImage("");
          pRef.current.style.backgroundImage = `url(${imageData.url})`;
          Cookies.set(
            "user",
            JSON.stringify({
              ...user,
              picture: imageData.url,
            })
          );
          dispatch({
            type: "UPDATEPICTURE",
            payload: imageData.url,
          });
          onPictureChange?.(imageData.url);
          showSuccess("Profile picture uploaded and post created successfully!");
          setShow(false);
          return true;
        }
        setError(new_post.message);
        return false;
      }
      setError(updated_picture);
      return false;
    } catch (error) {
      setError(
        error.response?.data?.message || "Failed to update profile picture"
      );
      return false;
    }
  };

  const handleSaveProfilePicture = async () => {
    try {
      setLoading(true);
      const img = await getCroppedImage();
      const blob = await fetch(img).then((b) => b.blob());
      const path = `${user.username}/profile_pictures`;
      const formData = new FormData();
      formData.append("file", blob);
      formData.append("path", path);
      const res = await uploadImages(formData, path, user.token);

      setPendingProfileUpdate(res[0]);
      setShowPostConfirmation(true);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setError(error.response?.data?.message || "Failed to upload image");
    }
  };

  const handleConfirmCreatePost = async () => {
    setShowPostConfirmation(false);
    setLoading(true);
    await updateProfilePictureWithPost(pendingProfileUpdate);
    setLoading(false);
    setPendingProfileUpdate(null);
  };

  const handleSkipPost = async () => {
    setShowPostConfirmation(false);
    setLoading(true);
    await updateProfilePictureOnly(pendingProfileUpdate);
    setLoading(false);
    setPendingProfileUpdate(null);
  };

  const handleCancelConfirmation = () => {
    setShowPostConfirmation(false);
    setPendingProfileUpdate(null);
  };

  const hasRemovablePicture =
    image &&
    image !== DEFAULT_PROFILE_PIC &&
    !String(image).includes("default_pic") &&
    Boolean(user?.picture);

  return (
    <>
      {showSuccessAlert && (
        <div className="profile_success_alert">
          <div className="profile_success_content">
            <i className="success_check_icon">✓</i>
            <span>{successMessage}</span>
          </div>
        </div>
      )}

      <input
        type="file"
        ref={uploadInputRef}
        hidden
        accept="image/jpeg,image/png,image/webp,image/gif,image/avif"
        onChange={handleReplaceImage}
      />
      <input
        type="file"
        ref={cameraInputRef}
        hidden
        accept="image/*"
        capture="user"
        onChange={handleReplaceImage}
      />

      <div className="postBox update_img">
        <div className="box_header">
          <div className="small_circle" onClick={() => setImage("")}>
            <i className="exit_icon"></i>
          </div>
          <span>Edit profile picture</span>
        </div>
        <div className="update_center">
          <div className="crooper" style={{ position: "relative" }}>
            <Cropper
              image={image}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={1 / 1}
              cropShape="round"
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
              onRotationChange={setRotation}
              showGrid={false}
              style={{
                containerStyle: {
                  background: "transparent",
                },
                mediaStyle: {
                  background: "transparent",
                },
              }}
            />
            <div className="profile_pic_camera_wrap" ref={cameraMenuRef}>
              <button
                type="button"
                className="profile_pic_camera_btn"
                aria-label="Change profile picture"
                title="Change profile picture"
                disabled={loading || removing}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowCameraMenu((prev) => !prev);
                }}
              >
                <i className="camera_filled_icon" aria-hidden="true"></i>
              </button>
              {showCameraMenu && (
                <div className="profile_pic_camera_menu" role="menu">
                  <button
                    type="button"
                    className="profile_pic_camera_menu_item"
                    role="menuitem"
                    onClick={handleUploadPhoto}
                  >
                    <i className="upload_icon" aria-hidden="true"></i>
                    <span>Upload photo</span>
                  </button>
                  {isMobile && (
                    <button
                      type="button"
                      className="profile_pic_camera_menu_item"
                      role="menuitem"
                      onClick={handleTakePhoto}
                    >
                      <i className="camera_icon" aria-hidden="true"></i>
                      <span>Take photo</span>
                    </button>
                  )}
                  {hasRemovablePicture && (
                    <button
                      type="button"
                      className="profile_pic_camera_menu_item profile_pic_camera_menu_item--danger"
                      role="menuitem"
                      disabled={removing}
                      onClick={handleRemoveProfilePicture}
                    >
                      <i className="trash_icon" aria-hidden="true"></i>
                      <span>{removing ? "Removing..." : "Remove photo"}</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="slider">
            <div className="slider_circle hover1" onClick={() => zoomOut()}>
              <i className="minus_icon"></i>
            </div>
            <input
              type="range"
              min={1}
              max={3}
              step={0.2}
              ref={slider}
              value={zoom}
              onChange={(e) => setZoom(e.target.value)}
            />
            <div className="slider_circle hover1" onClick={() => zoomIn()}>
              <i className="plus_icon"></i>
            </div>
            <div
              className="slider_circle hover1"
              onClick={rotateLeft}
              title="Rotate left"
            >
              <i
                className="refresh_icon"
                style={{ transform: "scaleX(-1)" }}
              ></i>
            </div>
            <div
              className="slider_circle hover1"
              onClick={rotateRight}
              title="Rotate right"
            >
              <i className="refresh_icon"></i>
            </div>
          </div>
        </div>
        <div className="flex_p_t">
          {accountPrivacy === "private" ? (
            <>
              <img
                src="/icons/lock.png"
                alt=""
                style={{ width: 16, height: 16, marginRight: 6 }}
              />
              Your profile picture is private
            </>
          ) : (
            <>
              <i className="public_icon"></i>
              Your profile picture is public
            </>
          )}
        </div>
        <div className="update_submit_wrap">
          <div className="blue_link" onClick={() => setImage("")}>
            Cancel
          </div>
          <button
            className="blue_btn"
            disabled={loading || removing}
            onClick={handleSaveProfilePicture}
          >
            {loading ? <PulseLoader color="#fff" size={5} /> : "Save"}
          </button>
        </div>
      </div>

      {showPostConfirmation && (
        <div className="blur profile_post_confirmation_overlay">
          <div className="postBox profile_post_confirmation_modal">
            <div className="box_header">
              <span>Create a publication with your new profile picture?</span>
            </div>
            <div className="profile_post_confirmation_content">
              <div className="profile_post_confirmation_image">
                <img
                  src={pendingProfileUpdate?.url}
                  alt="Profile preview"
                  style={{
                    width: 120,
                    height: 120,
                    objectFit: "cover",
                    borderRadius: "50%",
                  }}
                />
              </div>
              <p>
                Do you want to share your new profile picture as a publication
                so your friends can see it?
              </p>
              {description && (
                <div className="profile_post_confirmation_description">
                  <strong>Description:</strong> {description}
                </div>
              )}
            </div>
            <div className="profile_post_confirmation_actions">
              <button
                className="gray_btn"
                onClick={handleCancelConfirmation}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                className="light_gray_btn"
                onClick={handleSkipPost}
                disabled={loading}
              >
                {loading ? (
                  <PulseLoader color="#333" size={5} />
                ) : (
                  "Skip Publication"
                )}
              </button>
              <button
                className="blue_btn"
                onClick={handleConfirmCreatePost}
                disabled={loading}
              >
                {loading ? (
                  <PulseLoader color="#fff" size={5} />
                ) : (
                  "Yes, Create publication"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
